// D56 — the debug clip extractor. A rolling recording of the WebGL canvas (two ping-pong MediaRecorder
// segments of ≤30 s each ⇒ at export time there is always a complete previous segment plus the
// in-flight one) and a rolling GAME-STATE log (compact samples ~7/s + every match event). One key
// (F8) downloads: clip part 1 (older), clip part 2 (freshest), and the JSON log — "store every moment
// something odd happens". Debug tooling only; the viral-clip export feature builds on this later.
//
// The log core (ClipLog) is pure and unit-tested; the media shell no-ops in environments without
// MediaRecorder/captureStream (jsdom, old engines) so the game never depends on it.

export class ClipLog {
  private samples: unknown[] = [];
  private events: unknown[] = [];

  constructor(
    private readonly maxSamples = 500, // ~70 s at 7 samples/s
    private readonly maxEvents = 400,
  ) {}

  sample(s: unknown): void {
    this.samples.push(s);
    if (this.samples.length > this.maxSamples) this.samples.shift();
  }

  event(e: unknown): void {
    this.events.push(e);
    if (this.events.length > this.maxEvents) this.events.shift();
  }

  get sizes(): { samples: number; events: number } {
    return { samples: this.samples.length, events: this.events.length };
  }

  /** The export payload: caller meta + environment + the two rolling buffers (oldest → newest). */
  assemble(meta: Record<string, unknown>): string {
    return JSON.stringify(
      {
        meta: {
          ...meta,
          exportedAt: new Date().toISOString(),
          userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'n/a',
        },
        events: this.events,
        samples: this.samples,
      },
      null,
      1,
    );
  }

  reset(): void {
    this.samples.length = 0;
    this.events.length = 0;
  }
}

const SEGMENT_MS = 30_000;

function pickMime(): string {
  for (const m of ['video/webm;codecs=vp9', 'video/webm;codecs=vp8', 'video/webm']) {
    if (MediaRecorder.isTypeSupported(m)) return m;
  }
  return '';
}

function download(name: string, blob: Blob): void {
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 10_000);
}

export class ClipRecorder {
  readonly log = new ClipLog();
  private stream: MediaStream | null = null;
  private rec: MediaRecorder | null = null;
  private chunks: Blob[] = [];
  private prev: Blob | null = null;
  private timer = -1;
  private exporting = false;

  /** Start (or re-target after a scene rebuild) the rolling canvas recording. Safe to call anytime. */
  attach(canvas: HTMLCanvasElement): void {
    if (typeof MediaRecorder === 'undefined' || typeof canvas.captureStream !== 'function') return;
    this.detach();
    this.stream = canvas.captureStream(30);
    this.cycle();
  }

  detach(): void {
    if (this.timer >= 0) clearTimeout(this.timer);
    this.timer = -1;
    try {
      if (this.rec && this.rec.state !== 'inactive') this.rec.stop();
    } catch {
      /* already stopped */
    }
    this.rec = null;
    for (const t of this.stream?.getTracks() ?? []) t.stop();
    this.stream = null;
  }

  private cycle(): void {
    if (!this.stream) return;
    const chunks: Blob[] = [];
    const rec = new MediaRecorder(this.stream, { mimeType: pickMime(), videoBitsPerSecond: 6_000_000 });
    rec.ondataavailable = (e): void => {
      if (e.data && e.data.size > 0) chunks.push(e.data);
    };
    rec.onstop = (): void => {
      this.prev = new Blob(chunks, { type: rec.mimeType });
    };
    rec.start(1000);
    this.rec = rec;
    this.chunks = chunks;
    this.timer = window.setTimeout(() => {
      try {
        rec.stop(); // onstop banks it as `prev`
      } catch {
        /* torn down mid-cycle */
      }
      this.cycle();
    }, SEGMENT_MS);
  }

  /** Stop the in-flight segment and resolve its blob (null when recording is unavailable). */
  private finishCurrent(): Promise<Blob | null> {
    return new Promise((resolve) => {
      const rec = this.rec;
      if (!rec || rec.state === 'inactive') {
        resolve(null);
        return;
      }
      if (this.timer >= 0) clearTimeout(this.timer);
      this.timer = -1;
      const chunks = this.chunks;
      rec.addEventListener('stop', () => resolve(chunks.length ? new Blob(chunks, { type: rec.mimeType }) : null), { once: true });
      try {
        rec.stop();
      } catch {
        resolve(null);
      }
    });
  }

  /** F8: download clip part 1 (previous segment), part 2 (freshest), and the game-state log. */
  async export(meta: Record<string, unknown>): Promise<void> {
    if (this.exporting) return;
    this.exporting = true;
    try {
      const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      const older = this.prev; // bank BEFORE stopping (onstop overwrites prev)
      const fresh = await this.finishCurrent();
      if (older && fresh !== older) download(`fafi-clip-${stamp}-1.webm`, older);
      if (fresh) download(`fafi-clip-${stamp}-2.webm`, fresh);
      download(`fafi-clip-${stamp}-log.json`, new Blob([this.log.assemble(meta)], { type: 'application/json' }));
      if (this.stream) this.cycle(); // resume the rolling recording
    } finally {
      this.exporting = false;
    }
  }
}
